import { useState, useRef, useEffect, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import LoadingScreen from '../components/LoadingScreen';
import { PATH_AUTH } from '../routes/paths';
import {
    isSamePhone,
    parseLoginPhone,
    savePendingLogin,
    stripLoginPhone,
} from '../utils/loginPhoneLink';

// ----------------------------------------------------------------------

type AuthGuardProps = {
    children: ReactNode;
};

export default function AuthGuard({ children }: AuthGuardProps) {
    const { isAuthenticated, isInitialized, user, logout } = useAuth();

    const { pathname, search, hash } = useLocation();

    const [requestedLocation, setRequestedLocation] = useState<string | null>(null);

    // Distinguishes a cold visit to a deep link from an in-app logout. In-app
    // logouts (manual or session-timeout) must not carry the current screen
    // across; the timeout flow uses its own user-gated return intent.
    const wasAuthenticatedRef = useRef(false);

    // A `loginPhone` link: verify that phone, then land here without the param.
    const loginPhone = parseLoginPhone(search);
    const destination = `${pathname}${stripLoginPhone(search)}${hash}`;

    // Signed in as someone else: the link is for its phone's owner, so sign out
    // first. Once signed out, the branch below takes it to verify-code.
    const linkForAnotherUser =
        isInitialized &&
        isAuthenticated &&
        loginPhone !== null &&
        !isSamePhone(user?.phoneNumber, loginPhone);

    useEffect(() => {
        if (linkForAnotherUser) {
            logout();
        }
    }, [linkForAnotherUser, logout]);

    if (!isInitialized || linkForAnotherUser) {
        return <LoadingScreen />;
    }

    if (!isAuthenticated) {
        if (loginPhone) {
            // Explicit link: the destination is always kept, even after an
            // in-app logout. Consumed by VerifyCodeForm after a full-page
            // redirect, so it must live outside React state.
            sessionStorage.setItem('redirectPath', destination);
            savePendingLogin({ phoneNumber: loginPhone, autoSend: true, fromLink: true });
            // Replace, so the URL carrying the phone number leaves history.
            return <Navigate to={PATH_AUTH.verify} replace />;
        }
        if (!wasAuthenticatedRef.current) {
            sessionStorage.setItem('redirectPath', `${pathname}${search}${hash}`);
        }
        if (pathname !== requestedLocation) {
            setRequestedLocation(pathname);
        }
        return <Navigate to={PATH_AUTH.login} />;
    }

    wasAuthenticatedRef.current = true;

    // Already signed in as the link's phone: just drop the param.
    if (loginPhone) {
        return <Navigate to={destination} replace />;
    }

    if (requestedLocation && pathname !== requestedLocation) {
        setRequestedLocation(null);
        return <Navigate to={requestedLocation} />;
    }

    return <>{children}</>;
}
